export type ComputeOverflowFitInput = {
  /** Hideable item widths in display order. Items at the END of the array are hidden first. */
  hideableWidths: number[];
  alwaysVisibleWidths?: number[];
  /** Width of fixed leading content (e.g. reference children rendered before the hideable group). */
  fixedLeadingWidth?: number;
  /** Width of the overflow trigger button (shown when at least one hideable is hidden). */
  triggerWidth: number;
  gap: number;
  /** Total width available for the row. */
  budget: number;
};

export type ComputeOverflowFitResult = {
  visibleHideable: number;
  hiddenHideable: number;
};

export const computeOverflowFit = ({
  hideableWidths,
  alwaysVisibleWidths = [],
  fixedLeadingWidth = 0,
  triggerWidth,
  gap,
  budget,
}: ComputeOverflowFitInput): ComputeOverflowFitResult => {
  const totalHideable = hideableWidths.length;

  const widthFor = (visibleHideable: number): number => {
    const items: number[] = [];
    if (fixedLeadingWidth > 0) {
      items.push(fixedLeadingWidth);
    }
    for (let i = 0; i < visibleHideable; i++) {
      items.push(hideableWidths[i]);
    }
    if (visibleHideable < totalHideable) {
      items.push(triggerWidth);
    }
    for (const width of alwaysVisibleWidths) {
      items.push(width);
    }
    if (items.length === 0) {
      return 0;
    }
    const sum = items.reduce((accumulator, width) => accumulator + width, 0);
    return sum + (items.length - 1) * gap;
  };

  // 1px tolerance to absorb subpixel rounding between float widthFor sums and integer clientWidth.
  const tolerance = 1;
  let visible = totalHideable;
  while (visible > 0 && widthFor(visible) > budget + tolerance) {
    visible--;
  }
  return {
    visibleHideable: visible,
    hiddenHideable: totalHideable - visible,
  };
};
